/* eslint-disable no-param-reassign */ /* FIXME: just avoiding to deal with eslint issues after unignoring */
// @ts-nocheck -- file is fucked
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import moment from 'moment';

import type CurrencyAmount from 'Components/UI_Library/FormElements/CurrencyAmount';
import type { PTUserInfo } from './PTUserInfo.type';
import type { Company, User } from 'Backend/company/types';
import type { AttachedCompany } from 'Backend/users/getAttachedCompanies';

import { logger } from 'utils/logger/logger';
import { getAttachedCompanies } from 'Backend/users/utils/attachedCompanies/getAttachedCompanies';
import { getToken } from './Utils/authToken';
import { getCompanyStatus, setCompanyStatus } from './Utils/companyStatus';

TimeAgo.addLocale(en);
const timeAgo = new TimeAgo('en-US');

export const isExpiredToken = async (): Promise<boolean> => {
    try {
        // NOTE: at least for Okta, the refresh token doesn't seem to include an expiry on the token, the only way to
        // know is whether the refreshToken fails. That is the reason this has to be done asynchronously.
        const token = await getToken(0);
        return !token;
    } catch (error) {
        logger.error('Error checking if token is expired', { error });
        return true;
    }
};

export const b64toFile = (b64Data: string, filename: string, contentType: string) => {
    const sliceSize = 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }
    const file = new File(byteArrays, filename, { type: contentType });
    return file;
};

export const signoutWhenExpiredToken = async () => {
    const tokenHasExpired = await isExpiredToken().catch(() => true);

    if (
        tokenHasExpired &&
        window.location.pathname !== '/signout' &&
        window.location.pathname !== '/signin' &&
        window.location.pathname !== '/connect' &&
        window.location.pathname !== '/connect/signin' &&
        window.location.pathname !== '/signup' &&
        window.location.pathname !== '/referrals/keypay' &&
        window.location.pathname !== '/referrals/employmenthero' &&
        !window.location.pathname.startsWith('/referrals') &&
        !window.location.pathname.startsWith('/invites/') &&
        !window.location.pathname.startsWith('/kyc/') &&
        !window.location.pathname.startsWith('/verify-email') &&
        !window.location.pathname.startsWith('/forgot-pwd')
    ) {
        console.log('TRIGGERING SIGNOUT', window.location.pathname);
        window.location.pathname = '/signout';
    }
};

export function convertEmptyString(obj) {
    for (const prop in obj) {
        if (typeof obj[prop] === 'object') {
            convertEmptyString(obj[prop]);
        } else if (obj[prop] === '') {
            delete obj[prop];
        }
    }
    return obj;
}

export function upperCaseFirst(str: string) {
    if (str) {
        return str.charAt(0).toUpperCase() + str.substring(1);
    }
    return str;
}

export function toLowerCaseSafe(str: string) {
    if (typeof str === 'string') return str.toLowerCase();
    return str;
}

export function searchString(sourceStr: string, searchStr: string) {
    if (!(searchStr?.length > 0)) return true;

    return sourceStr?.toLowerCase().indexOf(searchStr?.toLowerCase()) > -1 || false;
}

const locale = navigator?.language || 'en-US';
/** Checks code is a string, if not defaults to AUD */
const getCurrency = (code?: string) => (code && typeof code === 'string' ? code : 'AUD');

/**
 * Formats the given amount with code using the Internationalisation API, with option to omit the symbol
 *
 * @deprecated In most cases you should use {@link CurrencyAmount} component instead.
 */
export function currencyFormat(amount?: number | string, code?: string, noSymbol?: boolean) {
    const totalAmount = parseFloat(amount, 10) || 0;
    const currency = getCurrency(code);
    const numberFormat = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
    });

    const formattedAmount = numberFormat.format(totalAmount);

    if (!noSymbol) return formattedAmount;

    const currencySymbol = getCurrencySymbol(numberFormat, totalAmount);
    return formattedAmount.replace(currencySymbol, '');
}

/** Uses the Internationalisation API .formatToParts function to extract the currency from the given NumberFormat with amount */
const getCurrencySymbol = (numberFormat: Intl.NumberFormat, amount = 0) => {
    const parts = numberFormat.formatToParts(amount);
    return (parts || []).find((i) => i.type === 'currency')?.value || '$';
};

/** Gets the symbol from the given passed code e.g. AUD will return $ */
export function getSymbolCurrency(code?: string) {
    const numberFormat = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: getCurrency(code),
        currencyDisplay: 'narrowSymbol',
    });
    return getCurrencySymbol(numberFormat);
}

export const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        /* eslint-disable-next-line no-type-assertion/no-type-assertion -- narrowing FileReader to `readAsDataURL` which returns result as string (| null before loaded or on error) */
        const reader = new FileReader() satisfies FileReader as Pick<
            FileReader,
            'onload' | 'onerror' | 'readAsDataURL'
        > & {
            result: Exclude<FileReader['result'], ArrayBuffer>;
        };

        reader.readAsDataURL(file);
        reader.onload = () =>
            resolve(
                reader.result! /* eslint-disable-line no-type-assertion/no-type-assertion, @typescript-eslint/no-non-null-assertion -- onload only executes once result has been successfully read and is no longer null */
            );
        reader.onerror = (error) => reject(error);
    });

/**
 * Get the time of the date relative to "now" in words.
 *
 * @param {Date} date
 */
const getTimeAgo = (date: Date | number) => {
    let timeFormatted = timeAgo.format(date);
    timeFormatted = timeFormatted.replace('minutes', 'mins');
    timeFormatted = timeFormatted.replace('minute', 'min');
    return timeFormatted;
};

const getCurrentCompanyName = () => {
    const companyId = getCurrentCompanyId();
    const attachedCompanies = getAttachedCompanies();
    return attachedCompanies.find((c) => c.companyId === companyId)?.companyName;
};

export const COMPANY_ID_LOCAL_STORAGE_KEY = 'PAYTRON-COMPANY-ID';
/** Sets the current company id as well as related information. */
const setCurrentCompanyId = (companyId: string) => {
    localStorage.setItem(COMPANY_ID_LOCAL_STORAGE_KEY, companyId);
    // Whenever current company is set (changes) we also want to update the company status and the logger context.
    setCompanyStatus(getAttachedCompanies().find((c) => c.companyId === companyId)?.companyStatus);
    logger.setContext({ ...logger.getContext(), companyId });
};
const getCurrentCompanyId = () => localStorage.getItem(COMPANY_ID_LOCAL_STORAGE_KEY) || '';

/** Updates the company status and ID in local storage from a query string. Relies on the user being logged in */
const getCompanyFromQueryParams = () => {
    const { search } = window.location;
    const params = new URLSearchParams(search);
    const companyId = params.get('companyid');

    // check that a companyID query exists and it exists in user permissions
    if (companyId && getPermissions(companyId)) {
        setCurrentCompanyId(companyId);
    }
};

const CURRENT_USER_LOCAL_STORAGE_KEY = 'PTUserInfo';

/**
 * Get information about the currently logged in user.
 *
 * Returns the information set by {@link setCurrentUserInfo}.
 */
export const getCurrentUserInfo = (): PTUserInfo | undefined => {
    const userRaw = localStorage.getItem(CURRENT_USER_LOCAL_STORAGE_KEY);
    try {
        return userRaw && userRaw !== ''
            ? (JSON.parse(userRaw) as PTUserInfo) // FIXME: validate that this is PTUserInfo?
            : undefined;
    } catch {
        logger.error('Failed to parse user from local storage', { userRaw });
        return undefined;
    }
};

/**
 * Set information about the currently logged in user.
 *
 * To read the information, use {@link getCurrentUserInfo}.
 */
export const setCurrentUserInfo = (userInfo: PTUserInfo) => {
    // Whenever current user info is set (changes) we also want to update the logger context.
    logger.setContext({
        ...logger.getContext(),
        ...({
            userId: userInfo.sub,
            user: { id: userInfo.sub, name: userInfo.name, email: userInfo.email },
        } satisfies LoggerContext),
    });

    localStorage.setItem(CURRENT_USER_LOCAL_STORAGE_KEY, JSON.stringify(userInfo));
};

const getCurrentUserId = () => getCurrentUserInfo()?.sub || '';

const getCurrentUserEmail = () => getCurrentUserInfo()?.email || '';

export const ON_BEHALF_OF_USER_ID_LOCAL_STORAGE_KEY = 'PAYTRON-ONBEHALFOF-USER-ID';

export const setCurrentOnBehalfOfUserId = (userId: string) => {
    if (!userId) {
        localStorage.removeItem(ON_BEHALF_OF_USER_ID_LOCAL_STORAGE_KEY);
    } else {
        localStorage.setItem(ON_BEHALF_OF_USER_ID_LOCAL_STORAGE_KEY, userId);
    }
};

export const getCurrentOnBehalfOfUserId = () => localStorage.getItem(ON_BEHALF_OF_USER_ID_LOCAL_STORAGE_KEY);

const isOwner = () => {
    const CompanyId = getCurrentCompanyId();
    const attachedCompanies = getAttachedCompanies();
    return attachedCompanies.find((c) => c.companyId === CompanyId)?.type === 'Owner';
};

const getPermissions = (companyId = getCurrentCompanyId()) => {
    const attachedCompanies = getAttachedCompanies();
    return attachedCompanies.find((c) => c.companyId === companyId) || undefined;
};

const isHasPermission = (group: keyof AttachedCompany['permissions'], role): boolean => {
    const CompanyId = getCurrentCompanyId();
    const attachedCompanies = getAttachedCompanies();

    const relevantPermissions = attachedCompanies.find((c) => c.companyId === CompanyId);
    if (!relevantPermissions) return false;

    if (relevantPermissions.type === 'Owner') return true;

    const { permissions } = relevantPermissions;

    if (typeof role === 'string') {
        return Boolean(permissions[group]?.[role]);
    }

    // Array of list actions
    const listValids = role.map((action) => permissions[group] !== undefined && Boolean(permissions[group][action]));
    return listValids.includes(true);
};

const formatBankCodeType = (bankCodeType: string) => {
    switch (bankCodeType) {
        case 'bsbCode':
            return 'BSB';
        case 'billerCode':
            return 'Biller code';
        case 'sortCode':
            return 'Sort code';
        case 'bicSwift':
            return 'BIC/SWIFT';
        case 'aba':
            return 'ABA';
        case 'ifsc':
            return 'IFSC';
        case 'payId':
            return 'PayID';
        case 'bankAndBranchCode':
            return 'Bank number and bank branch';
        default:
            return bankCodeType;
    }
};

const sortByUnixDate = (a, b, attribute) => {
    a = new Date(a[attribute] * 1000);
    b = new Date(b[attribute] * 1000);
    // force null and undefined to the bottom
    a = a === null || a === undefined ? new Date(0, 0, 0) : a;
    b = b === null || b === undefined ? new Date(0, 0, 0) : b;

    // force any string values to lowercase
    a = typeof a === 'string' ? a.toLowerCase() : a;
    b = typeof b === 'string' ? b.toLowerCase() : b;

    // Return either 1 or -1 to indicate a sort priority
    if (a < b) {
        return 1;
    }
    if (a > b) {
        return -1;
    }
    // returning 0 or undefined will use any subsequent column sorting methods or the row index as a tiebreaker
    return 0;
};

const isIndividual = (company: Company) => company?.EntityType === 'Individual';

const isMYOBPayment = (payment: { Source: string }) => payment && payment.Source === 'MYOB';

const isCombinedPayment = (payment: { GroupId: string }) => payment && payment.GroupId;

function numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** gets the ordinal format of a number e.g. nth(2) returns 'nd'  */
const nth = function (d: number) {
    if (typeof d === 'string') {
        d = parseInt(d);
    }
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

/**  Utilities for converting values to cents for new APIs */
const ZERO_DECIMAL_CURRENCIES = ['JPY', 'IDR', 'UGX'];
const convertDollarsToCents = (amount, currency) =>
    ZERO_DECIMAL_CURRENCIES.includes(currency) ? amount : amount * 100;

const convertCentsToDollars = (amount, currency) =>
    ZERO_DECIMAL_CURRENCIES.includes(currency) ? amount : amount / 100;

const isNonDecimalCurrency = (currency?: string) => ZERO_DECIMAL_CURRENCIES.includes(currency);

const approvedStatuses = ['APPROVED', 'APPROVED - CARDS ONLY'];

const paymentStatusToReadable = {
    SCHEDULED: 'Scheduled',
    READY: 'Processing',
    'NEEDS APPROVAL': 'Needs approval',
    'AWAITING FUNDS': 'Awaiting funds',
    REJECTED: 'System declined',
    DRAFT: 'Draft',
    APPROVED: 'Approved',
    DONE: 'Done',
    TERMINATED: 'User declined',
    'PAID EXTERNALLY': 'Paid externally',
};

const parseJSON = (str: string, fallback = []) => {
    try {
        return JSON.parse(str);
    } catch (e) {
        return fallback;
    }
};

const toSentenceCase = (camelCase: string) => {
    const lowerCase = camelCase
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .trim();
    const sentenceCase = lowerCase.charAt(0).toUpperCase() + lowerCase.slice(1);
    return sentenceCase;
};

const mergeRefs = (...refs) => {
    const filteredRefs = refs.filter(Boolean);
    if (!filteredRefs.length) return null;
    if (filteredRefs.length === 0) return filteredRefs[0];
    return (inst) => {
        for (const ref of filteredRefs) {
            if (typeof ref === 'function') {
                ref(inst);
            } else if (ref) {
                ref.current = inst;
            }
        }
    };
};

/** Converts PaymentType to a nice label e.g. priority -> SWIFT */
const getPaymentType = (PaymentType?: string) => {
    switch (PaymentType) {
        case 'regular':
        case 'Regular Payment - Local':
            return 'Paytron Direct';
        case 'priority':
        case 'Priority Payment - SWIFT':
            return 'SWIFT';
        case 'bpay':
        case 'BPay':
            return 'bpay';
        case 'payId':
        case 'PayId':
            return 'PayId';
        default:
            return 'Paytron Direct';
    }
};

/** Converts BankCodeType to a nice label e.g. bicSwift -> SWIFT */
const getBankCodeTypeDisplayName = (BankCodeType?: string) => {
    switch (BankCodeType) {
        case 'bicSwift':
            return 'SWIFT';
        case 'aba':
            return 'ABA';
        case 'sortCode':
            return 'Sort Code';
        case 'bsbCode':
            return 'BSB';
        case 'iban':
            return 'IBAN';
        case 'ifsc':
            return 'IFSC';
        case 'billerCode':
            return 'Biller Code';
        case 'branchCode':
            return 'Branch Code';
        case 'payId':
            return 'PayID';
        default:
            return BankCodeType;
    }
};

const upperCaseObjectKeys = (o) =>
    Object.fromEntries(Object.entries(o).map(([k, v]) => [k[0].toUpperCase() + k.slice(1), v]));

const getDirectDebitDelay = () => {
    /**
     * number of business days before dd can be completed
     * @returns {number}
     */
    const directDebitCuttoff = 14; // 2pm Sydney Time on weekdays
    const now = moment().tz('Australia/Sydney');

    // if saturday or sunday,
    if (now.weekday() === 0 || now.weekday() === 6) {
        return 4;
    }

    // if saturday or sunday,
    if (now.weekday() === 0 || now.weekday() === 6) {
        return 3;
    }
    // weekeday apply cuttoff
    return now.hour() >= directDebitCuttoff ? 4 : 3;
};

export {
    isMYOBPayment,
    isCombinedPayment,
    getTimeAgo,
    getCurrentCompanyId,
    getCurrentCompanyName,
    setCurrentCompanyId,
    getCompanyFromQueryParams,
    getCurrentUserId,
    getCurrentUserEmail,
    isHasPermission,
    getCompanyStatus,
    setCompanyStatus,
    formatBankCodeType,
    sortByUnixDate,
    isOwner,
    isIndividual,
    getPermissions,
    numberWithCommas,
    nth,
    convertDollarsToCents,
    convertCentsToDollars,
    isNonDecimalCurrency,
    toSentenceCase,
    ZERO_DECIMAL_CURRENCIES,
    approvedStatuses,
    paymentStatusToReadable,
    parseJSON,
    mergeRefs,
    getPaymentType,
    getBankCodeTypeDisplayName,
    upperCaseObjectKeys,
    getDirectDebitDelay,
    getToken,
};
/* eslint-enable no-param-reassign */
